import {momentToISO} from "../../../utils/dateUtils";
import React, {ChangeEvent, useEffect} from "react";
import DateField from "../../components/FormContent/DateField/DateField";
import InputField from "../../components/FormContent/InputField/InputField";
import TextAreaField from "../../components/FormContent/TextAreaField/TextAreaField";
import FormPageSection from "../../components/FormPageSection/FormPageSection";
import {BASIC_DETAILS_CONSTANTS as Labels} from "../../constants/LabelConstants";
import {EstateItemTypes, EstateToggle} from "../../model/EstateFlowchartTypes";
import {RadioGroup} from "../../../components";
import {Will} from "src/EstateFlowchart/model/Will";
import {booleanToRadioButton} from "../../../Assets/formHelpers";
import {Miscellaneous} from "../../model/Miscellaneous";

interface BasicDetailsSectionProps {
    basicInformation: EstateItemTypes;
    setBasicInformation: (estateItem: EstateItemTypes) => void;
    formType?: "Will" | "RevocableTrust" | "IrrevocableTrust" | "Miscellaneous";
}

function BasicDetailsSection({basicInformation, setBasicInformation, formType}: Readonly<BasicDetailsSectionProps>) {

    useEffect(() => {
        if (formType === "Miscellaneous") {
            const referenceElement = document.getElementById('visualTreatmentMisc-radio__label');
            const newSpan = document.createElement('span');
            newSpan.className = 'textarea-sub-text input__info margintop-sm visualTreatmentMisc-subtext';
            newSpan.textContent = 'Select how you would like this item to appear on the flowchart.\n' +
                'This changes only the visual representation of the card (e.g., as a trust, charity, or outright gift), not the underlying details of the item.';
            referenceElement?.after(newSpan);
        }
    }, []);

    return (
        <FormPageSection id={"basic-details-section"}
                         headerText={Labels.BASIC_DETAILS_SECTION_LABEL_TEXT}
                         showRequiredLabel={true}>

            <InputField label={Labels.FLOWCHART_NAME_LABEL_TEXT}
                        name={basicInformation.flowchartName}
                        onChange={(flowchartName) => {
                            setBasicInformation({
                                ...basicInformation,
                                flowchartName
                            })
                        }}
                        placeholder={Labels.FLOWCHART_NAME_PLACEHOLDER_TEXT}
                        subText={Labels.FLOWCHART_NAME_SUB_TEXT}
                        required={true}
                        autoComplete={"off"}
                        maxLength={60}/>

            {formType === "Will" && <RadioGroup
                name={'pourOverWill-radio'}
                label={Labels.POUR_OVER_WILL_LABEL_TEXT}
                className='vertical-radiogroup layout-data-entry-form__field flowchart-modal__field'
                layout='vertical'
                values={["Yes", "No"]}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setBasicInformation({
                        ...basicInformation,
                        pourOverWill: e.target.value === "Yes"
                    });
                }}
                selected={booleanToRadioButton((basicInformation as Will).pourOverWill)}
            />}

            {formType !== "Miscellaneous" && <><InputField
                label={
                    formType === "Will"
                        ? "Legal Name of Will"
                        : Labels.LEGAL_NAME_LABEL_TEXT
                }
                name={"legalName" in basicInformation ? basicInformation.legalName : ""}
                onChange={(legalName) => setBasicInformation({...basicInformation, legalName})}
                required={true}
            />

                <InputField label={Labels.NICKNAME_LABEL_TEXT}
                            name={"nickName" in basicInformation ? basicInformation.nickName : ""}
                            onChange={(nickName) => setBasicInformation({...basicInformation, nickName})}
                            subText={Labels.NICKNAME_SUB_TEXT}
                            required={true}/>
            </>}

            {formType === "Miscellaneous" &&
                <>
                    <InputField
                        label={"Name"}
                        name={"name" in basicInformation ? basicInformation.name : ""}
                        onChange={(name) => setBasicInformation({...basicInformation, name})}
                        required={true}
                    />
                    <RadioGroup
                        name={'inOrOutOfEstate-radio'}
                        label={Labels.ESTATE_TYPE_MISC_LABEL_TEXT}
                        required={true}
                        className='vertical-radiogroup layout-data-entry-form__field flowchart-modal__field'
                        layout='vertical'
                        values={["In Estate", "Out of Estate"]}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setBasicInformation({
                                ...basicInformation,
                                estateToggleType: e.target.value === EstateToggle.IN_ESTATE ? EstateToggle.IN_ESTATE : EstateToggle.OUT_OF_ESTATE
                            });
                        }}
                        selected={((basicInformation as Miscellaneous).estateToggleType)}
                    />

                    <RadioGroup
                        name={'visualTreatmentMisc-radio'}
                        label={Labels.VISUAL_TREATMENT_MISC_LABEL_TEXT}
                        required={true}
                        className='vertical-radiogroup layout-data-entry-form__field flowchart-modal__field'
                        layout='vertical'
                        values={["Miscellaneous", "Charity", "Outright", "Trust"]}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setBasicInformation({
                                ...basicInformation,
                                visualCardRepresentation: e.target.value
                            });
                        }}
                        selected={((basicInformation as Miscellaneous).visualCardRepresentation)}
                    />
                </>
            }

            {formType !== "Miscellaneous" && <>
                <DateField
                    date={"trustCreationDate" in basicInformation ? basicInformation.trustCreationDate : "creationDate" in basicInformation ? basicInformation.creationDate : ""}
                    label={Labels.DATE_CREATED_LABEL_TEXT}
                    required={true}
                    onChange={(date) => {
                        if (formType !== "Will") {
                            const trustCreationDate = date !== null ? momentToISO(date) : "";
                            setBasicInformation({...basicInformation, trustCreationDate});
                        } else {
                            const creationDate = date !== null ? momentToISO(date) : "";
                            setBasicInformation({...basicInformation, creationDate});
                        }
                    }}/>

                <TextAreaField input={"recordOfAmendment" in basicInformation ? basicInformation.recordOfAmendment : ""}
                               label={Labels.RECORD_OF_AMENDMENTS_LABEL_TEXT}
                               onChange={(recordOfAmendment) => setBasicInformation({
                                   ...basicInformation,
                                   recordOfAmendment
                               })}/>
            </>
            }
        </FormPageSection>
    );
}

export default BasicDetailsSection;